import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import {
    Plus, Search, Edit, Trash2, Users, Mail, X, Fingerprint
} from "lucide-react";

import {
    fetchConsultantsRequest,
    createConsultantRequest,
    updateConsultantRequest,
    deleteConsultantRequest,
} from "../../../../redux/actions/consultantsActions";

import "../RessourcesStyles/ConsultantManagement.css";

const emptyForm = {
    nomContact: "",
    emailContact: "",
    specialite: "",
    reference: "",
};

const normalize = (v) => String(v || "").trim().toLowerCase();

export default function ConsultantManagement() {
    const dispatch = useDispatch();

    const consultantsState = useSelector((s) => s.consultants || {});
    const consultants = consultantsState.consultants || [];
    const loading = !!consultantsState.loading;

    const [search, setSearch] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 5;

    const [open, setOpen] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [currentId, setCurrentId] = useState(null);
    const [form, setForm] = useState(emptyForm);

    useEffect(() => {
        dispatch(fetchConsultantsRequest());
    }, [dispatch]);

    const filtered = useMemo(() => {
        const q = normalize(search);
        return (consultants || []).filter((c) => {
            const n = normalize(c?.nomContact);
            const e = normalize(c?.emailContact);
            const s = normalize(c?.specialite);
            const r = normalize(c?.reference);
            return !q || n.includes(q) || e.includes(q) || s.includes(q) || r.includes(q);
        });
    }, [consultants, search]);

    const totalPages = Math.ceil(filtered.length / itemsPerPage) || 1;

    const paginatedConsultants = useMemo(() => {
        return filtered.slice(
            (currentPage - 1) * itemsPerPage,
            currentPage * itemsPerPage
        );
    }, [filtered, currentPage]);

    useEffect(() => {
        const total = Math.ceil(filtered.length / itemsPerPage) || 1;
        if (currentPage > total) {
            setCurrentPage(total);
        }
    }, [filtered.length, currentPage]);

    const openAdd = () => {
        setIsEdit(false);
        setCurrentId(null);
        setForm(emptyForm);
        setOpen(true);
    };

    const openEdit = (c) => {
        setIsEdit(true);
        setCurrentId(c.id);
        setForm({
            nomContact: c.nomContact || "",
            emailContact: c.emailContact || "",
            specialite: c.specialite || "",
            reference: c.reference || "",
        });
        setOpen(true);
    };

    const closeModal = () => {
        setOpen(false);
        setForm(emptyForm);
    };

    const validate = () => {
        if (!form.nomContact?.trim()) return "Nom du contact obligatoire";
        return null;
    };

    const onSubmit = () => {
        const err = validate();
        if (err) {
            Swal.fire({ icon: "warning", text: err, timer: 1800, showConfirmButton: false });
            return;
        }

        const payload = {
            nomContact: form.nomContact.trim(),
            emailContact: form.emailContact?.trim() || null,
            specialite: form.specialite?.trim() || null,
            reference: form.reference?.trim() || null,
        };

        if (isEdit && currentId) {
            dispatch(updateConsultantRequest(currentId, payload));
        } else {
            dispatch(createConsultantRequest(payload));
        }
        closeModal();
    };

    const onDelete = (id) => {
        Swal.fire({
            title: "Supprimer ?",
            text: "Cette action est irréversible",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Oui",
            cancelButtonText: "Annuler",
            confirmButtonColor: "#dc3545",
            cancelButtonColor: "#2f4595",
        }).then((res) => {
            if (res.isConfirmed) dispatch(deleteConsultantRequest(id));
        });
    };

    return (
        <div className="cns-page">
            <div className="cns-header">
                <div>
                    <h2>Gestion des consultants</h2>
                </div>
                <button className="cns-btn cns-btn-primary consultant-add-btn" onClick={openAdd}>
                    <Plus size={15} /> Ajouter Consultant
                </button>
            </div>

            <div className="cns-toolbar">
                <div className="cns-search-wrap">
                    <Search size={14} className="cns-search-icon" />
                    <input
                        type="text"
                        className="cns-search"
                        value={search}
                        onChange={e => {
                            setSearch(e.target.value);
                            setCurrentPage(1);
                        }}
                        placeholder="Rechercher par nom, email, spécialité..."
                    />
                </div>
                <div className="cns-toolbar-meta">
                    {loading ? "..." : `${filtered.length} consultant(s)`}
                </div>
            </div>

            <div className="cns-card">
                <div className="cns-table-wrap">
                    <table className="cns-table">
                        <thead>
                            <tr>
                                <th>Nom du consultant</th>
                                <th>Email</th>
                                <th>Spécialité</th>
                                <th>Référence</th>
                                <th style={{ textAlign: "right", width: 120 }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan={5} className="cns-loading">Chargement...</td>
                                </tr>
                            ) : filtered.length === 0 ? (
                                <tr>
                                    <td colSpan={5} className="cns-empty">Aucun consultant trouvé</td>
                                </tr>
                            ) : (
                                paginatedConsultants.map((c) => (
                                    <tr key={c.id}>
                                        <td>
                                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                                <Fingerprint size={14} style={{ color: "#dc3545" }} />
                                                <span style={{ fontWeight: 700, color: "#2f4595" }}>{c.nomContact}</span>
                                            </div>
                                        </td>
                                        <td>
                                            {c.emailContact ? (
                                                <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                                                    <Mail size={12} style={{ color: "#6b7280" }} />
                                                    {c.emailContact}
                                                </div>
                                            ) : "-"}
                                        </td>
                                        <td><span className="cns-badge cns-badge--blue">{c.specialite || "-"}</span></td>
                                        <td><span className="cns-badge cns-badge--grey">{c.reference || "-"}</span></td>
                                        <td style={{ textAlign: "right" }}>
                                            <div className="cns-actions">
                                                <button className="cns-icon-btn" title="Modifier" onClick={() => openEdit(c)}>
                                                    <Edit size={15} />
                                                </button>
                                                <button className="cns-icon-btn cns-icon-btn--danger" title="Supprimer" onClick={() => onDelete(c.id)}>
                                                    <Trash2 size={15} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
                {filtered.length > 0 && (
                    <div className="cicada-pagination" style={{ margin: "12px 14px" }}>
                        <div className="cicada-pagination-info">
                            Affichage de {(currentPage - 1) * itemsPerPage + 1} à {Math.min(currentPage * itemsPerPage, filtered.length)} sur {filtered.length} consultants
                        </div>

                        <div className="cicada-pagination-actions">
                            <button
                                type="button"
                                disabled={currentPage === 1}
                                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                            >
                                Précédent
                            </button>

                            <span>
                                Page {currentPage} / {totalPages}
                            </span>

                            <button
                                type="button"
                                disabled={currentPage === totalPages}
                                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                            >
                                Suivant
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* MODAL */}
            {open && (
                <div className="cns-modal-overlay" onClick={closeModal}>
                    <div className="cns-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="cns-modal-header">
                            <h3>
                                <Users size={16} style={{ color: "#dc3545" }} />
                                {isEdit ? "Modifier Consultant" : "Ajouter Consultant"}
                            </h3>
                            <button className="cns-x" onClick={closeModal}><X size={18} /></button>
                        </div>

                        <div className="cns-modal-body">
                            <div className="cns-grid">
                                <div className="cns-field">
                                    <label>Nom du contact *</label>
                                    <input
                                        value={form.nomContact}
                                        onChange={(e) => setForm({ ...form, nomContact: e.target.value })}
                                        placeholder="EX: Jean Dupont"
                                    />
                                </div>

                                <div className="cns-field">
                                    <label>Email professionnel</label>
                                    <input
                                        value={form.emailContact}
                                        onChange={(e) => setForm({ ...form, emailContact: e.target.value })}
                                        placeholder="jean.dupont@email.com"
                                    />
                                </div>

                                <div className="cns-field">
                                    <label>Spécialité</label>
                                    <input
                                        value={form.specialite}
                                        onChange={(e) => setForm({ ...form, specialite: e.target.value })}
                                        placeholder="EX: Management, IT..."
                                    />
                                </div>

                                <div className="cns-field">
                                    <label>Référence interne</label>
                                    <input
                                        value={form.reference}
                                        onChange={(e) => setForm({ ...form, reference: e.target.value })}
                                        placeholder="CNS-2026-001"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="cns-modal-footer">
                            <button className="cns-btn cns-btn-light" onClick={closeModal}>Annuler</button>
                            <button className="cns-btn cns-btn-primary" onClick={onSubmit}>
                                {isEdit ? "Enregistrer" : "Créer"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
